import { PlusOutlined } from "@ant-design/icons";
import { message } from "antd";
import Search from "antd/es/input/Search";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getExportExportSchedulesThunk,
    getSchedulerMyRoutesThunk,
} from "tm-shared/api";
import { buildRoutePath } from "tm-shared/utils";

import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import Meta from "@/components/Meta";
import { FloatingActionButton } from "@/components/UI/FloatingActionButton";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { clearCurrentScheduleList } from "@/store/slices/sv/sv.slice";

import ExportDropdown from "./components/ExportDropdown";
import RouteCard from "./components/RouteCard";

const RouteManagement = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [messageApi, contextHolder] = message.useMessage();

    const { offlineMode } = useAppSelector((state) => state.offline);

    const { id } = useAppSelector((state) => state.user);
    const { exportIndicator } = useAppSelector((state) => state.work);
    const { routeList } = useAppSelector((state) => state.sv);

    const handleExportSingleRoute = useCallback(
        async (agentId: string, routeName: string) => {
            await dispatch(getExportExportSchedulesThunk({ agentId }));
            messageApi.info(`Експорт маршруту "${routeName}" розпочато`);
        },
        [messageApi],
    );

    const handleExportAllRoutes = useCallback(
        async ({ key }: { key: string }) => {
            switch (key) {
                case "excel":
                    await dispatch(
                        getExportExportSchedulesThunk({ agentId: null }),
                    );
                    messageApi.info(
                        `Експорт усіх маршрутів торгових агентів розпочато`,
                    );
                    break;
            }
        },
        [messageApi],
    );

    const handleNavigateToRoute = useCallback(
        (routeId: string) => {
            if (offlineMode) return;

            navigate(buildRoutePath(ROUTES.RouteManagementEdit, { routeId }));
        },
        [navigate, offlineMode],
    );

    const handleCreateNewRoute = useCallback(() => {
        dispatch(clearCurrentScheduleList());
        navigate("new-route");
    }, [dispatch, navigate]);

    const filteredRoutes = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return Object.values(routeList).filter(
            (item) =>
                item.name.toLowerCase().includes(term) ||
                item?.agentFullName?.toLowerCase()?.includes(term),
        );
    }, [routeList, searchTerm]);

    useEffect(() => {
        const fetchMyRoutes = async () => {
            if (id && !offlineMode) {
                try {
                    setIsLoading(true);
                    await dispatch(getSchedulerMyRoutesThunk({ ownerId: id }));
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchMyRoutes();
    }, [dispatch, id]);

    return (
        <>
            <Meta title="Profile" />
            <Header
                title={"Редагування маршрутів"}
                titleLoading={isLoading}
                rightActions={
                    <ExportDropdown
                        isLoading={!!exportIndicator}
                        loadingLabel={exportIndicator.toString()}
                        menuItems={[{ key: "excel", label: "Експорт в Excel" }]}
                        onMenuClick={handleExportAllRoutes}
                    />
                }
            />

            {contextHolder}

            <MainContainer>
                <Search
                    placeholder="Шукати"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                    size="large"
                    style={{ marginBottom: 16 }}
                />
                {filteredRoutes.map((route) => (
                    <RouteCard
                        key={route.uuid}
                        id={route.uuid}
                        name={route.name}
                        marketCount={route.marketCount}
                        agentFullName={route.agentFullName}
                        disableExport={exportIndicator === "A"}
                        onClick={() => handleNavigateToRoute(route.uuid)}
                        onExportSingleRoute={() =>
                            handleExportSingleRoute(route.agentId, route.name)
                        }
                        onViewMarkets={() =>
                            console.log("Переглянути всі ТТ", route.agentId)
                        }
                        onDeleteRoute={() =>
                            console.log("Видалити маршрут", route.name)
                        }
                    />
                ))}
            </MainContainer>

            {!offlineMode && (
                <FloatingActionButton
                    onClick={handleCreateNewRoute}
                    icon={<PlusOutlined />}
                    right={16}
                    bottom={80}
                />
            )}
        </>
    );
};

export default RouteManagement;
